import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, BookOpen, Clock, LogIn, MessageCircle, Mail, Calendar, Users } from "lucide-react";
import { useLanguage, pick, waLink } from "@/i18n";
import { useAuth } from "@/auth";
import { useSEO } from "@/hooks/useSEO";
import SectionHeader from "@/components/SectionHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  inviata: "bg-medblue/10 text-medblue",
  attesa: "bg-terracotta/20 text-ink",
  confermata: "bg-sage/25 text-ink",
  completata: "bg-sage/40 text-ink",
  annullata: "bg-red-100 text-red-700",
};

function signInWithGoogle() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/companion";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Companion() {
  const { slug } = useParams();
  const { lang, t } = useLanguage();
  const { user } = useAuth();
  const [stories, setStories] = useState([]);
  const [story, setStory] = useState(null);
  const [requests, setRequests] = useState([]);

  useSEO(
    story ? `${pick(story.title, lang)} — Flegrea Companion` : `${t("companion_title")} — Flegrea Experience`,
    story ? pick(story.excerpt, lang) : t("companion_sub")
  );

  useEffect(() => {
    if (slug) {
      setStory(null);
      fetch(`${API}/stories/${slug}`).then((r) => (r.ok ? r.json() : null)).then(setStory).catch(() => {});
    } else {
      fetch(`${API}/stories`).then((r) => r.json()).then(setStories).catch(() => {});
    }
  }, [slug]);

  useEffect(() => {
    if (!user || slug) return;
    fetch(`${API}/me/requests`, { credentials: "include" }).then((r) => (r.ok ? r.json() : [])).then(setRequests).catch(() => {});
  }, [user, slug]);

  // READING VIEW
  if (slug) {
    if (!story) return <div className="py-40 text-center text-ink/40">{t("loading")}</div>;
    return (
      <div data-testid="story-page">
        <section className="relative h-[45vh] min-h-[320px] flex items-end overflow-hidden">
          <img src={story.image} alt={pick(story.title, lang)} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/45" />
          <div className="relative max-w-3xl mx-auto px-6 pb-12 w-full">
            <Link to="/companion" data-testid="back-to-companion" className="inline-flex items-center gap-2 text-ivory/70 text-xs uppercase tracking-[0.2em] mb-5 hover:text-ivory transition-colors duration-300">
              <ArrowLeft size={13} strokeWidth={1.5} /> {t("back_companion")}
            </Link>
            <h1 className="font-serif text-3xl md:text-5xl text-ivory tracking-tight leading-[1.05]" data-testid="story-title">{pick(story.title, lang)}</h1>
            <p className="mt-3 flex items-center gap-2 text-ivory/70 text-xs uppercase tracking-[0.2em]">
              <Clock size={13} strokeWidth={1.5} /> {story.minutes} {t("min_read")}
            </p>
          </div>
        </section>
        <article className="max-w-3xl mx-auto px-6 py-16">
          <p className="font-serif text-2xl italic text-medblue leading-snug mb-8">{pick(story.excerpt, lang)}</p>
          {pick(story.body, lang).split("\n\n").map((p, i) => (
            <p key={i} className="text-base md:text-lg text-ink/75 leading-relaxed mb-6">{p}</p>
          ))}
          <Link to={`/experiences?destination=${story.destination}`} data-testid="story-destination-link"
            className="inline-flex items-center gap-2 mt-6 border border-ink text-ink text-xs font-medium uppercase tracking-[0.2em] px-6 py-4 hover:bg-ink hover:text-ivory transition-colors duration-300">
            {t("view_destination")} →
          </Link>
        </article>
      </div>
    );
  }

  // COMPANION HOME
  return (
    <div className="py-16 lg:py-24" data-testid="companion-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <SectionHeader overline="Flegrea Experience" title={t("companion_title")} sub={t("companion_sub")} />
        <p className="mt-6 text-xs text-ink/40 uppercase tracking-[0.2em]" data-testid="companion-langs">
          Italiano · English · Français / Deutsch / Español — {t("langs_note")}
        </p>

        {/* READINGS */}
        <section className="mt-16" data-testid="readings-section">
          <h2 className="font-serif text-3xl text-ink mb-10 flex items-center gap-3">
            <BookOpen size={24} strokeWidth={1.25} className="text-medblue" /> {t("readings_title")}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12">
            {stories.map((s) => (
              <Link key={s.slug} to={`/companion/${s.slug}`} data-testid={`story-card-${s.slug}`} className="group block img-zoom">
                <div className="overflow-hidden aspect-[16/10] bg-stonelt">
                  <img src={s.image} alt={pick(s.title, lang)} loading="lazy" className="w-full h-full object-cover" />
                </div>
                <p className="mt-4 text-[10px] uppercase tracking-[0.2em] text-terracotta">{s.destination}</p>
                <h3 className="mt-1.5 font-serif text-2xl text-ink group-hover:text-medblue transition-colors duration-300 leading-tight">
                  {pick(s.title, lang)}
                </h3>
                <p className="mt-2 text-sm text-ink/55 leading-relaxed line-clamp-2">{pick(s.excerpt, lang)}</p>
                <p className="mt-3 flex items-center gap-1.5 text-xs text-ink/40">
                  <Clock size={12} strokeWidth={1.5} /> {s.minutes} {t("min_read")}
                </p>
              </Link>
            ))}
          </div>
        </section>

        {/* YOUR SERVICES */}
        <section className="mt-24" data-testid="your-services-section">
          <h2 className="font-serif text-3xl text-ink mb-4">{t("your_services")}</h2>
          {!user ? (
            <div className="border border-dashed border-stone2 p-10 text-center">
              <p className="text-ink/60">{t("account_signin_sub")}</p>
              <button onClick={signInWithGoogle} data-testid="companion-signin"
                className="mt-6 inline-flex items-center gap-2 bg-medblue text-ivory text-xs font-medium uppercase tracking-[0.2em] px-7 py-4 hover:bg-ink transition-colors duration-300">
                <LogIn size={15} strokeWidth={1.5} /> {t("sign_in")} — Google
              </button>
            </div>
          ) : requests.length === 0 ? (
            <div className="border border-dashed border-stone2 p-10 text-center">
              <p className="text-ink/50">{t("no_requests")}</p>
              <Link to="/experiences" className="mt-4 inline-block text-[11px] uppercase tracking-[0.2em] text-medblue">
                {t("discover_experiences")} →
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {requests.map((r) => (
                <div key={r.id} className="border border-stone2/60 p-5 flex flex-wrap items-center gap-x-6 gap-y-3" data-testid={`service-${r.id}`}>
                  <div className="min-w-[180px]">
                    <p className="font-serif text-xl text-ink">{r.type === "custom" ? t("custom_request") : pick(r.experience_title, lang)}</p>
                    <p className="text-xs text-ink/40 mt-1">{new Date(r.created_at).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB")}</p>
                  </div>
                  {r.type === "booking" && (
                    <>
                      <span className="flex items-center gap-2 text-sm text-ink/60"><Calendar size={14} strokeWidth={1.5} className="text-medblue" /> {r.date || "—"}</span>
                      <span className="flex items-center gap-2 text-sm text-ink/60"><Users size={14} strokeWidth={1.5} className="text-medblue" /> {r.participants}</span>
                    </>
                  )}
                  <span className={`text-[10px] font-semibold uppercase tracking-[0.15em] px-3 py-1.5 ${STATUS_STYLE[r.status] || STATUS_STYLE.inviata}`}>
                    {t(`status_${r.status}`)}
                  </span>
                  <a
                    href={waLink(`${t("wa_message")} ${r.type === "custom" ? t("custom_request") : pick(r.experience_title, lang)} — ${r.date || ""}`)}
                    target="_blank" rel="noopener noreferrer"
                    data-testid={`service-support-${r.id}`}
                    className="ml-auto inline-flex items-center gap-2 border border-medblue text-medblue text-[10px] font-medium uppercase tracking-[0.15em] px-4 py-2.5 hover:bg-medblue hover:text-ivory transition-colors duration-300"
                  >
                    <MessageCircle size={13} strokeWidth={1.5} /> {t("support_staff")}
                  </a>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* SUPPORT */}
        <section className="mt-20 bg-sand p-10 text-center" data-testid="companion-support">
          <h2 className="font-serif text-3xl text-ink">{t("support_title")}</h2>
          <p className="mt-3 text-sm text-ink/60 max-w-lg mx-auto">{t("support_sub")}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a href={waLink(t("wa_message") + " ")} target="_blank" rel="noopener noreferrer" data-testid="companion-whatsapp"
              className="inline-flex items-center gap-2 bg-medblue text-ivory text-xs font-medium uppercase tracking-[0.2em] px-7 py-4 hover:bg-ink transition-colors duration-300">
              <MessageCircle size={14} strokeWidth={1.5} /> WhatsApp
            </a>
            <a href="mailto:info@flegreaexperience.com" data-testid="companion-email"
              className="inline-flex items-center gap-2 border border-ink text-ink text-xs font-medium uppercase tracking-[0.2em] px-7 py-4 hover:bg-ink hover:text-ivory transition-colors duration-300">
              <Mail size={14} strokeWidth={1.5} /> Email
            </a>
          </div>
        </section>
      </div>
    </div>
  );
}
